/**
 * Utility to convert DocViewSchema sections + tabs into TabStructure format
 * 
 * This bridges the new DocViewSchema system with the existing TabRenderer.
 * TabRenderer expects TabStructure[] with sections that have columns/fields.
 * DocViewSchema has a flatter structure that we convert here.
 */

import type { DocViewVariantConfig, DocViewSectionConfig, DocViewTabConfig } from "../docViewSchemas/types";
import type { DocTypeSchema, DocFieldSchema } from "@/lib/erp/meta";
import type { TabStructure, SectionStructure, ColumnStructure } from "@/features/inventory/item-detail/utils/buildTabsFromMeta";
import { computeUnmappedFieldsFromSchema } from "../utils";
import { extractFieldsFromSystemTab, getSystemViewTabs } from "./extractFieldsFromSystemTabs";

/**
 * Convert DocViewSchema sections and tabs into TabStructure for TabRenderer
 * 
 * @param viewConfig - DocViewVariantConfig from schema
 * @param meta - DocType meta schema
 * @param formData - Current form data (for conditional visibility)
 * @returns TabStructure[] compatible with TabRenderer
 */
export function convertSchemaToTabs(
  viewConfig: DocViewVariantConfig,
  meta: DocTypeSchema,
  formData: Record<string, any>
): TabStructure[] {
  const { sections, tabs } = viewConfig;

  // Create a map for quick section lookup
  const sectionMap = new Map(sections.map((s) => [s.id, s]));

  // Create meta field lookup
  const metaFieldMap = new Map(meta.fields.map((f) => [f.fieldname, f]));

  // Convert section to SectionStructure
  const convertSection = (section: DocViewSectionConfig): SectionStructure | null => {
    // For widget-based sections, create a simple structure
    // The widget will handle its own rendering
    if (section.widgetId) {
      return {
        name: section.id,
        label: section.label || section.id,
        columns: [], // Widget handles its own content
        _widgetId: section.widgetId, // Pass widget ID for TabRenderer
        description: section.description, // Preserve description for widgets
      };
    }

    // Special case: If section has _pullFromSystemTab, extract fields from System View tab
    // This allows Experience View to automatically pull all fields from the corresponding System View tab
    const pullFromTab = (section as any)._pullFromSystemTab;
    if (pullFromTab) {
      // Try multiple possible tab names (fieldname, label, common variations)
      let systemTabFields = extractFieldsFromSystemTab(meta, pullFromTab);
      
      // If not found, try common variations
      if (systemTabFields.length === 0) {
        const variations = [
          pullFromTab.toLowerCase(),
          pullFromTab.replace(/-/g, '_'),
          pullFromTab.replace(/_/g, '-'),
        ];
        
        for (const variant of variations) {
          systemTabFields = extractFieldsFromSystemTab(meta, variant);
          if (systemTabFields.length > 0) {
            if (import.meta.env.DEV) {
              console.warn(`[schemaToTabs] Tab "${pullFromTab}" not found, but found "${variant}"`);
            }
            break;
          }
        }
      }
      
      // If still not found, log available tabs for debugging
      if (systemTabFields.length === 0 && import.meta.env.DEV) {
        const allTabs = getSystemViewTabs(meta);
        console.warn(
          `[schemaToTabs] Tab "${pullFromTab}" not found for section "${section.id}". ` +
          `Available tabs: ${allTabs.map(t => `"${t.name}" (${t.label})`).join(', ')}`
        );
      }
      
      if (import.meta.env.DEV && systemTabFields.length > 0) {
        console.log(`[schemaToTabs] ✅ Pulled ${systemTabFields.length} fields from System View tab "${pullFromTab}" for section "${section.id}":`, {
          tabName: pullFromTab,
          fieldnames: systemTabFields.map(f => f.fieldname),
        });
      }

      if (systemTabFields.length > 0) {
        // Create column structure based on layout
        const columns: ColumnStructure[] =
          section.layout === "two-column"
            ? [
                { name: `${section.id}-col1`, label: "Column 1", fields: systemTabFields.slice(0, Math.ceil(systemTabFields.length / 2)) },
                { name: `${section.id}-col2`, label: "Column 2", fields: systemTabFields.slice(Math.ceil(systemTabFields.length / 2)) },
              ]
            : [{ name: `${section.id}-col1`, label: "Column 1", fields: systemTabFields }];

        return {
          name: section.id,
          label: section.label || section.id,
          columns,
          description: section.description, // Preserve description
        };
      }
      
      // If no fields found, return null (section will be skipped)
      return null;
    }

    // For field-based sections, build columns from fields
    if (section.fields && section.fields.length > 0) {
      const fields: DocFieldSchema[] = section.fields
        .map((fieldname) => metaFieldMap.get(fieldname))
        .filter((f): f is DocFieldSchema => f !== undefined);

      if (fields.length === 0) {
        return null; // Skip empty sections
      }

      // Create column structure based on layout
      const columns: ColumnStructure[] =
        section.layout === "two-column"
          ? [
              { name: `${section.id}-col1`, label: "Column 1", fields: fields.slice(0, Math.ceil(fields.length / 2)) },
              { name: `${section.id}-col2`, label: "Column 2", fields: fields.slice(Math.ceil(fields.length / 2)) },
            ]
          : [{ name: `${section.id}-col1`, label: "Column 1", fields }];

      return {
        name: section.id,
        label: section.label || section.id,
        columns,
        description: section.description, // Preserve description
      };
    }

    // Empty section (might be for unmapped fields)
    return {
      name: section.id,
      label: section.label || section.id,
      columns: [],
      _collectUnmapped: true, // Signal to collect unmapped fields
      description: section.description, // Preserve description
    };
  };

  // Check conditional visibility for a tab
  const isTabVisible = (tab: DocViewTabConfig): boolean => {
    if (!tab.conditionalVisibility) {
      return true;
    }

    const { field, value } = tab.conditionalVisibility;

    // Special handling for variants tab
    if (field === "has_variants_or_is_variant") {
      const hasVariants = formData?.has_variants === 1 || formData?.has_variants === true;
      // Frappe returns "" for unset Link fields — treat as not set
      const rawVariantOf = formData?.variant_of;
      const isVariant = typeof rawVariantOf === "string" ? !!rawVariantOf.trim() : !!rawVariantOf;
      const result = hasVariants || isVariant;
      return result;
    }

    const fieldValue = formData?.[field];

    // Handle various comparison types
    if (typeof value === "boolean") {
      const result = fieldValue === value || fieldValue === (value ? 1 : 0);
      return result;
    }
    if (typeof value === "number") {
      // For number comparisons, handle both number and boolean field values
      // has_variants can be 1 (number) or true (boolean) in ERPNext
      let result: boolean;
      if (field === "has_variants" && value === 1) {
        result = fieldValue === 1 || fieldValue === true;
      } else {
        result = fieldValue === value || fieldValue === Boolean(value);
      }
      return result;
    }
    return fieldValue === value;
  };

  // If tabs are defined, use them
  if (tabs && tabs.length > 0) {
    const result: TabStructure[] = [];

    // Compute unmapped fields once for all tabs
    const { unmappedFieldnames } = computeUnmappedFieldsFromSchema(meta, viewConfig);
    const unmappedFields = meta.fields.filter(f => 
      unmappedFieldnames.includes(f.fieldname)
    );

    // Debug logging (dev only)
    if (import.meta.env.DEV) {
      console.group(`[schemaToTabs] Unmapped Fields Debug - ${meta.name}`);
      console.log('Total meta fields:', meta.fields.length);
      console.log('Unmapped fieldnames count:', unmappedFieldnames.length);
      console.log('Unmapped fields (with meta):', unmappedFields.length);
      if (unmappedFields.length > 0) {
        console.log('Sample unmapped fields:', unmappedFields.slice(0, 10).map(f => f.fieldname));
      }
      console.groupEnd();
    }

    // Track whether unmapped fields were actually rendered
    let unmappedFieldsRendered = false;

    for (const tab of tabs) {
      // Check conditional visibility
      const isVisible = isTabVisible(tab);
      
      if (!isVisible) {
        continue;
      }

      // Get sections for this tab
      const tabSections: SectionStructure[] = [];
      let foundCollectUnmapped = false;

      for (const sectionId of tab.sectionIds) {
        const section = sectionMap.get(sectionId);
        
        if (!section) {
          if (import.meta.env.DEV) {
            console.warn(
              `[schemaToTabs] Section "${sectionId}" referenced in tab "${tab.id}" not found in sections`
            );
          }
          continue;
        }

        const converted = convertSection(section);
        
        if (converted) {
          // Enhanced debug for more-info section
          if (sectionId === 'more-info' && import.meta.env.DEV) {
            console.log(`[schemaToTabs] Processing more-info section:`, {
              sectionId,
              hasCollectUnmapped: converted._collectUnmapped,
              foundCollectUnmapped,
              unmappedFieldsCount: unmappedFields.length,
              willPopulate: converted._collectUnmapped && !foundCollectUnmapped && unmappedFields.length > 0,
              currentColumns: converted.columns.length,
            });
          }

          // If this section should collect unmapped fields, populate it
          if (converted._collectUnmapped && !foundCollectUnmapped && unmappedFields.length > 0) {
            if (import.meta.env.DEV) {
              console.log(`[schemaToTabs] ✅ Populating _collectUnmapped section "${converted.name}" with ${unmappedFields.length} fields`);
              console.log(`[schemaToTabs] First 5 fields:`, unmappedFields.slice(0, 5).map(f => f.fieldname));
            }
            // v1: Simple approach - fill first _collectUnmapped section with all unmapped fields
            // Keep fields in meta.fields order (preserves original grouping)
            converted.columns = [{
              name: `${converted.name}-col1`,
              label: "Column 1",
              fields: unmappedFields,
            }];
            foundCollectUnmapped = true; // Only populate first _collectUnmapped section per tab
            unmappedFieldsRendered = true; // Mark that we've rendered unmapped fields
            
            if (import.meta.env.DEV) {
              console.log(`[schemaToTabs] ✅ After population:`, {
                sectionName: converted.name,
                columnsCount: converted.columns.length,
                fieldsInColumn: converted.columns[0]?.fields?.length,
              });
            }
          }
          tabSections.push(converted);
        }
      }

      if (tabSections.length > 0 || tab.sectionIds.length > 0) {
        const tabResult = {
          name: tab.id,
          label: tab.label,
          sections: tabSections,
        };
        result.push(tabResult);
      }
    }

    // -----------------------------------------
    // FINAL SAFETY NET: ensure unmapped fields are visible
    // DISABLED: Users can switch to System View to see all fields
    // -----------------------------------------
    // if (unmappedFields.length > 0 && !unmappedFieldsRendered && result.length > 0) {
    //   const targetTab = result[result.length - 1]; // Attach to last tab by default

    //   if (import.meta.env.DEV) {
    //     console.warn(
    //       `[schemaToTabs] ⚠️ No _collectUnmapped section found for ${meta.name}. ` +
    //       `Attaching auto "More Info" section with ${unmappedFields.length} fields ` +
    //       `to tab "${targetTab.name}". ` +
    //       `Add an empty section in DocViewSchema (e.g. id: "more-info") ` +
    //       `to control placement.`
    //     );
    //   }

    //   targetTab.sections.push({
    //     name: "more-info-auto",
    //     label: "More Info",
    //     columns: [
    //       {
    //         name: "more-info-auto-col1",
    //         label: "Column 1",
    //         fields: unmappedFields,
    //       },
    //     ],
    //   });
    // }

    // Final verification logging
    if (import.meta.env.DEV && unmappedFields.length > 0) {
      console.group(`[schemaToTabs] Final Tab Structure Verification - ${meta.name}`);
      console.log('Unmapped fields rendered:', unmappedFieldsRendered);
      console.log('Total tabs:', result.length);
      
      // Find more-info tab
      const moreInfoTab = result.find(t => t.name === 'more-info');
      if (moreInfoTab) {
        console.log('✅ More Info tab found:', {
          name: moreInfoTab.name,
          sectionsCount: moreInfoTab.sections.length,
        });
        
        const moreInfoSection = moreInfoTab.sections.find(s => s.name === 'more-info' || s.name === 'more-info-auto');
        if (moreInfoSection) {
          console.log('✅ More Info section found:', {
            name: moreInfoSection.name,
            columnsCount: moreInfoSection.columns.length,
            fieldsCount: moreInfoSection.columns.reduce((sum, col) => sum + (col.fields?.length || 0), 0),
            hasCollectUnmapped: moreInfoSection._collectUnmapped,
          });
          if (moreInfoSection.columns.length > 0 && moreInfoSection.columns[0].fields) {
            console.log('First 5 fields in section:', moreInfoSection.columns[0].fields.slice(0, 5).map(f => f.fieldname));
          }
        } else {
          console.warn('⚠️ More Info section NOT found in tab');
        }
      } else {
        console.warn('⚠️ More Info tab NOT found in result');
      }
      console.groupEnd();
    }

    return result;
  }

  // No tabs defined - put all sections in a single "Details" tab
  const allSections: SectionStructure[] = sections
    .map((section) => convertSection(section))
    .filter((s): s is SectionStructure => s !== null);

  // Populate _collectUnmapped sections if any exist
  // DISABLED: Users can switch to System View to see all fields
  // const { unmappedFieldnames } = computeUnmappedFieldsFromSchema(meta, viewConfig);
  // const unmappedFields = meta.fields.filter(f => 
  //   unmappedFieldnames.includes(f.fieldname)
  // );

  // if (unmappedFields.length > 0) {
  //   let populated = false;

  //   // Find first section with _collectUnmapped and populate it
  //   for (const section of allSections) {
  //     if (section._collectUnmapped && section.columns.length === 0) {
  //       section.columns = [{
  //         name: `${section.name}-col1`,
  //         label: "Column 1",
  //         fields: unmappedFields,
  //       }];
  //       populated = true;
  //       break; // Only populate first _collectUnmapped section
  //     }
  //   }

    // Final fallback: no _collectUnmapped section → append one
    // DISABLED: Users can switch to System View to see all fields
    // if (!populated) {
    //   if (import.meta.env.DEV) {
    //     console.warn(
    //       `[schemaToTabs] No _collectUnmapped section found for ${meta.name} (no-tabs path). ` +
    //       `Appending auto "More Info" section with ${unmappedFields.length} fields.`
    //     );
    //   }

    //   allSections.push({
    //     name: "more-info-auto",
    //     label: "More Info",
    //     columns: [
    //       {
    //         name: "more-info-auto-col1",
    //         label: "Column 1",
    //         fields: unmappedFields,
    //       },
    //     ],
    //   });
    // }
  // }

  return [
    {
      name: "details",
      label: "Details",
      sections: allSections,
    },
  ];
}

